import * as React from 'react';
import { IProfileResult } from '../../../../data/providers/reports/profileReportProvider';
import { Entity } from '../../../../data/types';
import { roundValue } from '../../../../utils/math';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { Card } from '../../../shared/card/Card';
import { CardHeader } from '../../../shared/card/CardHeader';
import { Metric } from '../../../shared/Metric';
import { Notable } from '../../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../../shared/pdf/Pdf';
import { colors } from '../../colors';
import { DataCell, DataColumn, LabelCell } from '../../utils/Table';

interface IProps {
  result: IProfileResult;
  pdfMode: EPdfMode;
  entity: Entity;
}

export const ProfileResult = (props: IProps): React.ReactElement => {
  return (
    <Notable pdf={isPdf(props.pdfMode)}>
      <Card
        style={{ marginTop: 'var(--grid-gap)' }}
        inverse={shouldInvert(props.pdfMode)}
      >
        <Column>
          <CardHeader surface={24}>
            <div>
              <div>{props.result.testMeta}</div>
              <div>{props.result.metric.metric.label}</div>
            </div>
          </CardHeader>
          <Row style={{ height: '100%' }} shouldCollapse>
            <DataColumn>
              <LabelCell style={{ backgroundColor: colors[0] }}>
                <div>{`${props.entity.name} Mean`}</div>
                <div>
                  <div>({`${props.result.targetCount} Tests`})</div>
                  <div> {props.result.targetTimeframe}</div>
                </div>
              </LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.target?.mean,
                    props.result.target?.roundingPrecision
                  )}
                  units={props.result.target?.units}
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell style={{ backgroundColor: colors[1] }}>
                <div>Population Mean</div>
                <div>
                  <div>({`${props.result.populationCount} Tests`})</div>
                  <div>{props.result.populationTimeframe}</div>
                </div>
              </LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.population?.mean,
                    props.result.population?.roundingPrecision
                  )}
                  units={props.result.population?.units}
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell center>Z Score</LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(props.result.zscore, 2)}
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell center>% Difference</LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(props.result.percentDifference, 2)}
                  units='%'
                  isSecondary
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell center last>
                Total Difference
              </LabelCell>
              <DataCell lastInRow>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.change,
                    props.result.target?.roundingPrecision
                  )}
                  units={props.result.target?.units}
                  isSecondary
                />
              </DataCell>
            </DataColumn>
          </Row>
        </Column>
      </Card>
    </Notable>
  );
};
